import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { ArrowForward } from '@mui/icons-material';

interface Refund {
    id: number;
    terminalId: number;
    terminalName?: string;
    sum: number;
    dateTime: string;
    state: string;
    refundCode?: string;
}

interface VoucherOperationReference {
    rrn: string;
    terminalId: number;
    isInferred: boolean;
}

interface VoucherNode {
    refund: Refund;
    creationOperation: VoucherOperationReference | null;
    closeOperation: VoucherOperationReference | null;
}

interface VoucherOperation {
    rrn: string;
    terminalId: number;
    inputRefundIds: number[];
    outputRefundIds: number[];
    inferredInputRefundIds: number[];
    inputSum: number;
    outputSum: number;
    usedSum: number | null;
    isAmountComplete: boolean;
    hasUnresolvedInputs: boolean;
}

interface VoucherRelation {
    parentRefundId: number | null;
    childRefundId: number;
    unresolvedParentRefundCode: string | null;
}

export interface VoucherGraph {
    rootRefundId: number | null;
    operation?: VoucherOperationReference | null;
    vouchers: VoucherNode[];
    operations: VoucherOperation[];
    relations: VoucherRelation[];
    warnings: string[];
}

interface RefundTreeDialogProps {
    open: boolean;
    onClose: () => void;
    selectedRefundId: number | null;
    graph: VoucherGraph | null;
    loading: boolean;
    statusLabels: Record<string, string>;
}

function voucherLabel(graph: VoucherGraph, refundId: number): string {
    const node = graph.vouchers.find(item => item.refund.id === refundId);
    return node?.refund.refundCode ? `#${refundId} · ${node.refund.refundCode}` : `#${refundId}`;
}

const VoucherChips: React.FC<{
    graph: VoucherGraph;
    ids: number[];
    inferredIds?: number[];
    emptyLabel: string;
}> = ({ graph, ids, inferredIds = [], emptyLabel }) => {
    if (ids.length === 0) {
        return <Typography variant="body2" color="text.secondary">{emptyLabel}</Typography>;
    }

    const inferred = new Set(inferredIds);
    return (
        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {ids.map(id => (
                <Chip
                    key={id}
                    size="small"
                    color={graph.rootRefundId === id ? 'primary' : 'default'}
                    variant={inferred.has(id) ? 'outlined' : 'filled'}
                    label={`${voucherLabel(graph, id)}${inferred.has(id) ? ' (по parentVouchers)' : ''}`}
                />
            ))}
        </Stack>
    );
};

const OperationCard: React.FC<{ graph: VoucherGraph; operation: VoucherOperation }> = ({ graph, operation }) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="subtitle1" fontWeight={700}>Заказ {operation.rrn}</Typography>
                    <Typography variant="caption" color="text.secondary">Терминал {operation.terminalId}</Typography>
                </Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip size="small" label={`Вход: ${operation.inputSum.toFixed(2)}`} />
                    <Chip size="small" label={`Остаток: ${operation.outputSum.toFixed(2)}`} />
                    <Chip
                        size="small"
                        color={operation.isAmountComplete ? 'success' : 'default'}
                        label={operation.usedSum !== null
                            ? `Использовано: ${operation.usedSum.toFixed(2)}`
                            : 'Использовано: недостаточно данных'}
                    />
                </Stack>
            </Box>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
                    gap: 2,
                    alignItems: 'center'
                }}
            >
                <Box>
                    <Typography variant="caption" color="text.secondary">Погашенные ваучеры</Typography>
                    <VoucherChips
                        graph={graph}
                        ids={operation.inputRefundIds}
                        inferredIds={operation.inferredInputRefundIds}
                        emptyLabel="У заказа нет входных ваучеров"
                    />
                </Box>
                <ArrowForward color="action" sx={{ display: { xs: 'none', md: 'block' } }} />
                <Box>
                    <Typography variant="caption" color="text.secondary">Созданные ваучеры-остатки</Typography>
                    <VoucherChips
                        graph={graph}
                        ids={operation.outputRefundIds}
                        emptyLabel="Остаток не создавался — полное погашение"
                    />
                </Box>
            </Box>

            {operation.hasUnresolvedInputs && (
                <Alert severity="warning">Не все входные ваучеры удалось найти по parentVouchers.</Alert>
            )}
        </Stack>
    </Paper>
);

const VoucherTable: React.FC<{
    graph: VoucherGraph;
    statusLabels: Record<string, string>;
}> = ({ graph, statusLabels }) => (
    <TableContainer component={Paper} variant="outlined">
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Ваучер</TableCell>
                    <TableCell>Сумма</TableCell>
                    <TableCell>Статус</TableCell>
                    <TableCell>Создан</TableCell>
                    <TableCell>Погашен</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {graph.vouchers.map(node => (
                    <TableRow key={node.refund.id} selected={graph.rootRefundId === node.refund.id}>
                        <TableCell>
                            <Typography variant="body2" fontWeight={600}>{voucherLabel(graph, node.refund.id)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {node.refund.terminalName || `Терминал ${node.refund.terminalId}`} ·{' '}
                                {new Date(node.refund.dateTime).toLocaleString()}
                            </Typography>
                        </TableCell>
                        <TableCell>{node.refund.sum.toFixed(2)}</TableCell>
                        <TableCell>{statusLabels[node.refund.state] || node.refund.state}</TableCell>
                        <TableCell>
                            {node.creationOperation
                                ? `${node.creationOperation.rrn} · терминал ${node.creationOperation.terminalId}`
                                : 'Операция неизвестна'}
                        </TableCell>
                        <TableCell>
                            {node.closeOperation
                                ? <>
                                    {node.closeOperation.rrn} · терминал {node.closeOperation.terminalId}
                                    {node.closeOperation.isInferred && ' (определено по остатку)'}
                                </>
                                : 'Не погашен или операция неизвестна'}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    </TableContainer>
);

export const RefundTreeDialog: React.FC<RefundTreeDialogProps> = ({
    open,
    onClose,
    selectedRefundId,
    graph,
    loading,
    statusLabels
}) => (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
        <DialogTitle>Граф возвратов{selectedRefundId !== null ? ` для ваучера #${selectedRefundId}` : ''}</DialogTitle>
        <DialogContent dividers>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : graph ? (
                <Stack spacing={3}>
                    <Typography variant="body2" color="text.secondary">
                        Ваучеров: {graph.vouchers.length} · заказов: {graph.operations.length}
                    </Typography>

                    {graph.warnings.map((warning, index) => (
                        <Alert key={`${warning}-${index}`} severity="warning">{warning}</Alert>
                    ))}

                    <Box>
                        <Typography variant="h6" gutterBottom>Цепочка заказов</Typography>
                        <Stack spacing={1.5}>
                            {graph.operations.length > 0
                                ? graph.operations.map(operation => (
                                    <OperationCard
                                        key={`${operation.terminalId}-${operation.rrn}`}
                                        graph={graph}
                                        operation={operation}
                                    />
                                ))
                                : <Alert severity="info">Для ваучера не найдены данные об операциях.</Alert>}
                        </Stack>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="h6" gutterBottom>Ваучеры</Typography>
                        <VoucherTable graph={graph} statusLabels={statusLabels} />
                    </Box>

                    {graph.relations.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                            Дополнительно найдено явных связей parentVouchers: {graph.relations.length}.
                        </Typography>
                    )}
                </Stack>
            ) : (
                <Alert severity="error">Граф возвратов не загружен.</Alert>
            )}
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Закрыть</Button></DialogActions>
    </Dialog>
);
